import {
  Intent as BlueprintIntent,
  MenuDivider as BlueprintMenuDivider,
  Callout,
} from '@blueprintjs/core';
import {
  Flash,
  Lightning,
  OverflowMenuHorizontal,
  User,
} from '@carbon/icons-react';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Body,
  Button,
  Heading,
  Menu,
  MenuItem,
  MenuItems,
} from 'vet-bones/design-system/components';

import { useAppDispatch } from 'src/ui/app/hooks';
import { Loading } from 'src/ui/components/Loading';
import { formatTotalUnits } from 'src/ui/components/sdu-dashboard/aggregations';
import {
  ORGANIZATION_DASHBOARD_CARD_ENDPOINTS,
  ORGANIZATION_DASHBOARD_CARD_MEMBERS,
  ORGANIZATION_DASHBOARD_CARD_UNITS,
  ORGANIZATION_DASHBOARD_CONTAINER,
  ORGANIZATION_DASHBOARD_INVITE_MEMBER,
  ORGANIZATION_DASHBOARD_MEMBER_VISIBILITY_NOTICE,
  ORGANIZATION_DASHBOARD_MENU,
  ORGANIZATION_DASHBOARD_MENU_DELETE,
  ORGANIZATION_DASHBOARD_MENU_EDIT,
  ORGANIZATION_DASHBOARD_MENU_LEAVE,
  ORGANIZATION_DASHBOARD_MENU_SSO,
  ORGANIZATION_DASHBOARD_MENU_TRANSFER,
} from 'src/ui/constants/testIds';
import { OrganizationDashboardCard } from 'src/ui/containers/organization/components/OrganizationDashboardCard';
import { OrganizationMembersTable } from 'src/ui/containers/organization/OrganizationMemberTable';
import { DialogType, openedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  useGetTotalUnitUsageQuery,
  useListOrganizationConnectionsQuery,
} from 'src/ui/graph/types';
import {
  useIsOrganizationAdmin,
  useIsOrganizationOwner,
  useOrganization,
} from 'src/ui/hooks/organization';
import * as copy from 'src/ui/templates/copy';
import { buildDashboardRoute } from 'src/ui/utils/buildRoute';

export const OrganizationDashboard: React.VFC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { org } = useParams<{ org: string }>();

  const {
    isLoadingInvitations,
    isLoadingMembers,
    organization,
    membersByEmail,
  } = useOrganization(org);
  const isAdmin = useIsOrganizationAdmin(org);
  const isOwner = useIsOrganizationOwner(org);

  const {
    data: connectionsData,
    isLoading: isLoadingConnections,
  } = useListOrganizationConnectionsQuery(graphQLClient, { org_domain: org });

  const { data: totalUnitsData } = useGetTotalUnitUsageQuery(graphQLClient, {
    org_domain: org,
  });

  const membersCount = Object.keys(membersByEmail).length;
  const endpointsCount = connectionsData?.listConnections?.length ?? 0;
  const totalUnitsValue = formatTotalUnits(
    totalUnitsData?.getTotalUnitUsage?.totalUnitsConsumed ?? 0
  );

  const handleEndpoints = () => {
    history.push(buildDashboardRoute(org, 'connections'));
  };

  const handleUnits = () => {
    history.push(buildDashboardRoute(org, 'units'));
  };

  const handleEditOrganization = () => {
    dispatch(openedDialog({ type: DialogType.EDIT_ORGANIZATION }));
  };

  const handleTransferOrganization = () => {
    dispatch(openedDialog({ type: DialogType.TRANSFER_ORGANIZATION }));
  };

  const handleLeaveOrganization = () => {
    dispatch(openedDialog({ type: DialogType.LEAVE_ORGANIZATION }));
  };

  const handleDeleteOrganization = () => {
    dispatch(openedDialog({ type: DialogType.DELETE_ORGANIZATION }));
  };

  const handleConfigureSso = () => {
    dispatch(openedDialog({ type: DialogType.CONFIGURE_ORGANIZATION_SSO }));
  };

  const handleInviteMember = () => {
    dispatch(openedDialog({ type: DialogType.INVITE_ORGANIZATION_MEMBERS }));
  };

  if (!organization) {
    return null;
  }

  if (isLoadingInvitations || isLoadingMembers || isLoadingConnections) {
    return <Loading />;
  }

  const membersRestricted = organization.membersHiddenFromMembers === true;
  const hideMemberList = membersRestricted && !isAdmin;

  return (
    <div
      className="organization-dashboard"
      data-testid={ORGANIZATION_DASHBOARD_CONTAINER}
    >
      <div className="organization-dashboard-header">
        <div className="organization-dashboard-header-info">
          <Heading size="xl" weight="semibold">
            {organization.name}
          </Heading>
          {organization.description ? (
            <Body
              className="organization-dashboard-header-description"
              size="md"
            >
              {organization.description}
            </Body>
          ) : null}
        </div>
        <div className="organization-dashboard-header-actions">
          {isAdmin ? (
            <Button
              onClick={handleInviteMember}
              size="md"
              testId={ORGANIZATION_DASHBOARD_INVITE_MEMBER}
              variant="tertiary-accent"
            >
              {copy.components.organization.dashboard.inviteMember}
            </Button>
          ) : null}
          <Menu
            leftIcon={OverflowMenuHorizontal}
            menu={
              <MenuItems className="organization-dashboard-menu-items">
                {isAdmin ? (
                  <MenuItem
                    leftIcon={null}
                    onClick={handleEditOrganization}
                    testId={ORGANIZATION_DASHBOARD_MENU_EDIT}
                    text={
                      copy.components.organization.dashboard.editOrganization
                    }
                  />
                ) : null}
                {isAdmin && organization.isSsoAllowed ? (
                  <MenuItem
                    leftIcon={null}
                    onClick={handleConfigureSso}
                    testId={ORGANIZATION_DASHBOARD_MENU_SSO}
                    text={copy.components.organization.dashboard.configureSso}
                  />
                ) : null}
                {isOwner ? (
                  <MenuItem
                    leftIcon={null}
                    onClick={handleTransferOrganization}
                    testId={ORGANIZATION_DASHBOARD_MENU_TRANSFER}
                    text={
                      copy.components.organization.dashboard
                        .transferOrganization
                    }
                  />
                ) : null}
                {isAdmin || isOwner ? (
                  <BlueprintMenuDivider className="vb-menu-divider" />
                ) : null}
                <MenuItem
                  leftIcon={null}
                  onClick={handleLeaveOrganization}
                  testId={ORGANIZATION_DASHBOARD_MENU_LEAVE}
                  text={
                    copy.components.organization.dashboard.leaveOrganization
                  }
                />
                {isOwner ? (
                  <MenuItem
                    leftIcon={null}
                    onClick={handleDeleteOrganization}
                    testId={ORGANIZATION_DASHBOARD_MENU_DELETE}
                    text={
                      copy.components.organization.dashboard.deleteOrganization
                    }
                  />
                ) : null}
              </MenuItems>
            }
            size="md"
            testId={ORGANIZATION_DASHBOARD_MENU}
          />
        </div>
      </div>

      <div className="organization-dashboard-cards">
        {hideMemberList ? null : (
          <OrganizationDashboardCard
            icon={User}
            subtitle={
              copy.components.organization.dashboard.cards.members.subtitle
            }
            testId={ORGANIZATION_DASHBOARD_CARD_MEMBERS}
            title={copy.components.organization.dashboard.cards.members.title}
            value={String(membersCount)}
          />
        )}
        <OrganizationDashboardCard
          icon={Flash}
          onClick={handleUnits}
          subtitle={copy.components.organization.dashboard.cards.units.subtitle}
          testId={ORGANIZATION_DASHBOARD_CARD_UNITS}
          title={copy.components.organization.dashboard.cards.units.title}
          value={totalUnitsValue}
        />
        <OrganizationDashboardCard
          icon={Lightning}
          onClick={handleEndpoints}
          subtitle={
            copy.components.organization.dashboard.cards.endpoints.subtitle
          }
          testId={ORGANIZATION_DASHBOARD_CARD_ENDPOINTS}
          title={copy.components.organization.dashboard.cards.endpoints.title}
          value={String(endpointsCount)}
        />
      </div>

      {membersRestricted && isAdmin ? (
        <Callout
          data-testid={ORGANIZATION_DASHBOARD_MEMBER_VISIBILITY_NOTICE}
          intent={BlueprintIntent.PRIMARY}
          title={copy.components.organization.dashboard.memberVisibility.title}
        >
          {copy.components.organization.dashboard.memberVisibility.body}
        </Callout>
      ) : null}

      {hideMemberList ? null : <OrganizationMembersTable />}
    </div>
  );
};
